/**
 * Multi-scale envelope pyramid computation and decimation utilities for
 * waveform rendering.
 *
 * A pyramid consists of several levels-of-detail (LODs), each summarizing
 * the raw PCM samples at a coarser resolution. Each bin in a level stores
 * the min, max, and RMS amplitude of the samples it covers, which allows
 * fast, visually accurate waveform rendering at any zoom level without
 * re-scanning the full sample buffer.
 *
 * LOD tiers:
 *   - LOD 0: 1 sample per bin (raw, no decimation)
 *   - LOD 1: 16 samples per bin
 *   - LOD 2: 256 samples per bin
 *   - LOD 3: 4096 samples per bin
 *
 * The pyramid supports incremental updates as new audio samples arrive
 * during a live recording, avoiding full recomputation on every chunk.
 */
/** Samples-per-bin factor for each LOD tier, in ascending order. */
export const LOD_FACTORS = [1, 16, 256, 4096];
const INT16_MAX = 32768;
/** Normalize a 16-bit PCM sample to the range [-1, 1]. */
function normalize(sample) {
    return sample / INT16_MAX;
}
/**
 * Compute LOD 0 (raw, 1:1) min/max/RMS arrays from a PCM buffer.
 * At LOD 0, min === max === normalized sample value, and rms is the
 * absolute value of the sample.
 */
function computeLod0(pcm) {
    const len = pcm.length;
    const min = new Float32Array(len);
    const max = new Float32Array(len);
    const rms = new Float32Array(len);
    for (let i = 0; i < len; i++) {
        const v = normalize(pcm[i]);
        min[i] = v;
        max[i] = v;
        rms[i] = Math.abs(v);
    }
    return { samplesPerPoint: 1, min, max, rms };
}
/**
 * Decimate a finer level into a coarser level by grouping `factor` entries
 * from the source level into one entry in the output level. `factor` is
 * relative to the source level's own bin size (i.e. the ratio between the
 * two levels' `samplesPerPoint`).
 */
function decimateLevel(source, factor) {
    const srcLen = source.min.length;
    const outLen = Math.ceil(srcLen / factor);
    const min = new Float32Array(outLen);
    const max = new Float32Array(outLen);
    const rms = new Float32Array(outLen);
    for (let i = 0; i < outLen; i++) {
        const start = i * factor;
        const end = Math.min(start + factor, srcLen);
        let binMin = Infinity;
        let binMax = -Infinity;
        let sumSquares = 0;
        let count = 0;
        for (let j = start; j < end; j++) {
            if (source.min[j] < binMin)
                binMin = source.min[j];
            if (source.max[j] > binMax)
                binMax = source.max[j];
            sumSquares += source.rms[j] * source.rms[j];
            count++;
        }
        min[i] = count > 0 ? binMin : 0;
        max[i] = count > 0 ? binMax : 0;
        rms[i] = count > 0 ? Math.sqrt(sumSquares / count) : 0;
    }
    return {
        samplesPerPoint: source.samplesPerPoint * factor,
        min,
        max,
        rms,
    };
}
/**
 * Build a full multi-tier envelope pyramid from a raw PCM buffer, computing
 * LOD 0 through the coarsest tier defined by `LOD_FACTORS`.
 */
export function buildEnvelopePyramid(sessionId, pcm) {
    const lod0 = computeLod0(pcm);
    const levels = [lod0];
    for (let i = 1; i < LOD_FACTORS.length; i++) {
        const relativeFactor = LOD_FACTORS[i] / LOD_FACTORS[i - 1];
        levels.push(decimateLevel(levels[i - 1], relativeFactor));
    }
    return {
        sessionId,
        levels,
        sourceSampleCount: pcm.length,
        updatedAt: Date.now(),
    };
}
/**
 * Incrementally extend an existing envelope pyramid with newly appended PCM
 * samples, avoiding recomputation of already-processed regions. This works
 * correctly as long as `newPcm` is a contiguous continuation of the audio
 * previously used to build `pyramid` (i.e. it directly follows
 * `pyramid.sourceSampleCount` samples already incorporated).
 */
export function appendToEnvelopePyramid(pyramid, newPcm) {
    if (newPcm.length === 0)
        return pyramid;
    const newLod0 = computeLod0(newPcm);
    const lod0 = pyramid.levels[0];
    const mergedLod0 = {
        samplesPerPoint: 1,
        min: concatFloat32(lod0.min, newLod0.min),
        max: concatFloat32(lod0.max, newLod0.max),
        rms: concatFloat32(lod0.rms, newLod0.rms),
    };
    const levels = [mergedLod0];
    for (let i = 1; i < LOD_FACTORS.length; i++) {
        const relativeFactor = LOD_FACTORS[i] / LOD_FACTORS[i - 1];
        levels.push(decimateLevel(levels[i - 1], relativeFactor));
    }
    return {
        sessionId: pyramid.sessionId,
        levels,
        sourceSampleCount: pyramid.sourceSampleCount + newPcm.length,
        updatedAt: Date.now(),
    };
}
function concatFloat32(a, b) {
    const out = new Float32Array(a.length + b.length);
    out.set(a, 0);
    out.set(b, a.length);
    return out;
}
/**
 * Select the optimal LOD level index for rendering a waveform window into a
 * canvas of the given pixel width.
 *
 * @param pyramid The envelope pyramid to select a level from.
 * @param visibleSampleCount Number of source samples visible within the
 *   current zoom window (i.e. `endSample - startSample`).
 * @param pixelWidth Width, in pixels, of the canvas region being rendered.
 * @returns Index into `pyramid.levels` of the best-fit LOD.
 */
export function selectLodLevel(pyramid, visibleSampleCount, pixelWidth) {
    if (pixelWidth <= 0 || visibleSampleCount <= 0)
        return 0;
    // Ideal samples-per-pixel ratio for this zoom window.
    const samplesPerPixel = visibleSampleCount / pixelWidth;
    // Pick the finest level whose samplesPerPoint does not exceed the
    // ideal samples-per-pixel ratio (to avoid visual under-sampling),
    // falling back to the coarsest available level if none qualify.
    let bestIndex = 0;
    for (let i = 0; i < pyramid.levels.length; i++) {
        if (pyramid.levels[i].samplesPerPoint <= samplesPerPixel) {
            bestIndex = i;
        }
        else {
            break;
        }
    }
    return bestIndex;
}
/**
 * Extract the slice of a given LOD level's min/max/RMS arrays covering the
 * requested source sample range `[startSample, endSample)`.
 */
export function getLevelRange(level, startSample, endSample) {
    const startBin = Math.max(0, Math.floor(startSample / level.samplesPerPoint));
    const endBin = Math.min(level.min.length, Math.ceil(endSample / level.samplesPerPoint));
    if (endBin <= startBin) {
        return {
            min: new Float32Array(0),
            max: new Float32Array(0),
            rms: new Float32Array(0),
        };
    }
    return {
        min: level.min.subarray(startBin, endBin),
        max: level.max.subarray(startBin, endBin),
        rms: level.rms.subarray(startBin, endBin),
    };
}
